import React from 'react';
import {
  AppBar,
  Toolbar,
  Grid,
  CircularProgress,
  Hidden,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { PrimaryButton } from '../common';
import MenuIcon from '@mui/icons-material/Menu';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UserAccount from '../UserAccount';

const FormHeaderComponent = ({
  // Form data
  formTitle,
  formLogo,

  // State flags
  hasAutoSaveEnabled,
  canSaveSubmission,
  isTest,
  readOnly,
  pageProcessing,
  previewMode,
  sended,
  isTerminated,
  openMainMenu,
  anchorEl,

  // Page navigation
  pagesMeta,
  visitedPages,
  currentIndex,

  // Event handlers
  onSave,
  onMenuToggle,
  onMenuClose,
  onPageNavigation,

  // Additional props for flexibility
  className = 'app-bar',
}) => {
  const renderLogo = () => {
    const logoSrc =
      formLogo === 'DELETE' || formLogo === ''
        ? '/images/default-image-form.png'
        : formLogo;

    return (
      <Grid className="form-logo-wrapper">
        <img alt="logo" className="form-logo imageForm" src={logoSrc} />
      </Grid>
    );
  };

  const renderSaveButton = () => {
    if ((!sended && canSaveSubmission) || isTest) {
      return (
        <PrimaryButton
          onClick={onSave}
          disabled={
            (pageProcessing || !canSaveSubmission || previewMode || readOnly) &&
            !isTest
          }
          startIcon={<SaveIcon />}
        >
          {isTest ? 'Save Test Values' : 'Save'}
          {pageProcessing && (
            <CircularProgress className="buttonProgress" size={20} />
          )}
        </PrimaryButton>
      );
    }
    return null;
  };

  const renderMobileMenu = () => {
    return (
      <Menu
        open={openMainMenu}
        anchorEl={anchorEl}
        onClose={onMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Save Button MenuItem */}
        {((!sended && canSaveSubmission) || isTest) && (
          <MenuItem>
            <PrimaryButton
              onClick={onSave}
              disabled={
                (pageProcessing ||
                  !canSaveSubmission ||
                  previewMode ||
                  readOnly) &&
                !isTest
              }
              startIcon={<SaveIcon />}
            >
              {isTest ? 'Save Test Values' : 'Save'}
              {pageProcessing && (
                <CircularProgress className="buttonProgress" size={20} />
              )}
            </PrimaryButton>
          </MenuItem>
        )}

        {/* User Account MenuItem */}
        <MenuItem>
          <UserAccount />
        </MenuItem>

        {/* Page Navigation MenuItems */}
        {pagesMeta.map((aPage, index) => {
          const canNavigateToPage =
            visitedPages.has(index) || parseInt(currentIndex) + 1 === index;

          return (
            <MenuItem
              key={index}
              className={
                index === parseInt(currentIndex) ? 'menu-item-current' : ''
              }
              onClick={
                canNavigateToPage ? () => onPageNavigation(index) : undefined
              }
              style={{
                cursor: canNavigateToPage ? 'pointer' : 'default',
                opacity: canNavigateToPage ? 1 : 0.6,
              }}
            >
              {aPage.pageTitle}
            </MenuItem>
          );
        })}
      </Menu>
    );
  };

  return (
    <>
      <AppBar position="sticky" color="inherit" className={className}>
        <Toolbar variant="dense" className="form-toolbar">
          <Grid container direction="row" alignItems="center" spacing={1}>
            {/* Left Section - Logo and Title */}
            <Grid item xs={8} md={8}>
              <Grid
                container
                direction="row"
                alignItems="center"
                justifyContent="flex-start"
              >
                <Grid item className="form-logo-container">
                  {renderLogo()}
                  <Grid className="form-title-container">{formTitle}</Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Right Section - Controls */}
            <Grid item xs={4} md={4}>
              <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                {/* Desktop Controls */}
                <Hidden mdDown>
                  {/* AutoSave Indicator */}
                  {hasAutoSaveEnabled && (
                    <Grid className="autosaveGrid">
                      <Tooltip
                        title={
                          <Typography variant="body2">
                            {
                              'Your work is automatically saved every 30 seconds'
                            }
                          </Typography>
                        }
                      >
                        <div className="autosave-indicator">
                          <CheckCircleIcon className="autosave-icon" />
                          <Typography
                            variant="caption"
                            className="autosave-text"
                          >
                            Auto-saved
                          </Typography>
                        </div>
                      </Tooltip>
                    </Grid>
                  )}

                  {/* Save Button */}
                  <Grid item>{renderSaveButton()}</Grid>

                  {/* User Account */}
                  <Grid item>
                    <UserAccount />
                  </Grid>
                </Hidden>

                {/* Mobile Menu Button */}
                <Hidden mdUp>
                  <Grid item className="menu-icon-button">
                    <IconButton
                      edge="start"
                      aria-label="menu"
                      size="large"
                      onClick={onMenuToggle}
                    >
                      <MenuIcon />
                      {renderMobileMenu()}
                    </IconButton>
                  </Grid>
                </Hidden>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default FormHeaderComponent;
